// "This supplier has looked at that brief" — recorded in two places.
//
// The authoritative record is brief_recipients.changes_seen_at, written when
// the supplier opens a brief. But that write is a read-receipt: it must never
// block opening a brief, so it can't be allowed to throw, and anything that
// stops it landing (an RLS refusal, a missing grant, an offline moment) would
// otherwise leave the dashboard alert stuck exactly as it was before —
// which is the bug this was supposed to fix, reappearing silently.
//
// So the same fact is also kept in localStorage and the two are merged on
// read, newest wins. The DB copy is what makes it work across devices; the
// local copy is what makes it work at all when the write is refused.
//
// This is a UI dismissal aid, not a record anyone relies on: losing it shows
// an alert twice, which is the safe direction to fail.

const KEY = 'sp2.changesSeen';

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Private mode, quota, or corrupt JSON — behave as if nothing was stored.
    return {};
  }
};

// Record that this recipient row has just been looked at.
export function markChangesSeen(recipientId, iso = new Date().toISOString()) {
  if (!recipientId) return;
  try {
    const all = readAll();
    all[recipientId] = iso;
    window.localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Nothing to do — the DB copy is the real one.
  }
}

// Epoch ms of the last look at this recipient row, taking whichever of the
// stored and local values is newer. 0 when never looked at.
export function changesSeenAt(recipient) {
  if (!recipient) return 0;
  const fromDb = recipient.changes_seen_at ? new Date(recipient.changes_seen_at).getTime() : 0;
  const local = readAll()[recipient.id];
  const fromLocal = local ? new Date(local).getTime() : 0;
  return Math.max(Number.isFinite(fromDb) ? fromDb : 0, Number.isFinite(fromLocal) ? fromLocal : 0);
}

// True when `iso` is a change the supplier hasn't looked at yet.
export const isNewerThanSeen = (iso, recipient) =>
  !!iso && new Date(iso).getTime() > changesSeenAt(recipient);
